/**
 * Auth bootstrap hook — validates the current Firebase session on app mount.
 *
 * Calls `getIdToken(true)` to force-refresh the token. If the Firebase
 * session is invalid (expired, revoked), logs out and clears state.
 *
 * Returns `{ isValidating: boolean }` so callers can show a loader.
 */
import { useEffect, useState, useRef } from 'react'
import { useAuthStore } from '../store'
import { authService } from '../auth'
import {
  firebaseLogout,
  getCurrentFirebaseUser,
  getIdToken,
  waitForAuthStateReady,
} from '../firebase/auth'

interface AuthBootstrapOptions {
  loadTenant?: boolean
}

class SessionRejected extends Error {}

/** Whether a failed check means the session is not valid (signed out, or
 *  refused by the API), as opposed to the API being unreachable or failing. */
function isRejection(err: unknown): boolean {
  if (err instanceof SessionRejected) return true
  const status = (err as { response?: { status?: number } })?.response?.status
  return status === 401 || status === 403
}

export function useAuthBootstrap(
  options: AuthBootstrapOptions = {},
): { isValidating: boolean } {
  const { loadTenant = true } = options
  const [isValidating, setIsValidating] = useState(true)
  const ranRef = useRef(false)

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isImpersonating = useAuthStore((s) => s.isImpersonating)
  const accessToken = useAuthStore((s) => s.accessToken)

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    if (!isAuthenticated) {
      setIsValidating(false)
      return
    }

    // Support sessions authenticate with an internally issued access token,
    // so Firebase bootstrap validation does not apply.
    if (isImpersonating && accessToken) {
      setIsValidating(false)
      return
    }

    const validate = async () => {
      try {
        await waitForAuthStateReady()
        const firebaseUser = getCurrentFirebaseUser()
        const token = await getIdToken(true)
        if (!firebaseUser || !token) {
          await firebaseLogout()
          useAuthStore.getState().logout()
          return
        }

        const store = useAuthStore.getState()
        store.setFirebaseAuth(firebaseUser.uid)

        const user = await authService.getCurrentUser()
        if (loadTenant && user.permissions?.includes('platform:admin')) {
          throw new SessionRejected('Super admin sessions must use the admin portal')
        }

        store.setUser(user)
        if (loadTenant) {
          const tenant = await authService.getCurrentTenant()
          store.setTenant(tenant)
        } else {
          store.setTenant(null)
        }
      } catch (err) {
        // Only a rejected session ends it. The API being down, restarting or
        // erroring is not the user signing out: they keep the session they
        // have (persisted user and tenant) and the next request tries again.
        if (isRejection(err)) {
          await firebaseLogout()
          useAuthStore.getState().logout()
        }
      } finally {
        setIsValidating(false)
      }
    }

    validate()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { isValidating }
}
